import React, { useContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StorageIcon from '@mui/icons-material/Storage';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import HardwareIcon from '@mui/icons-material/Hardware';
import VideocamIcon from '@mui/icons-material/Videocam';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import IconButton from '@mui/material/IconButton';
import LogoutIcon from '@mui/icons-material/Logout';

import Dashboard from './components/Dashboard';
import AdminLogin from './components/AdminLogin';
import AdminRegister from './components/AdminRegister'; // New import

import PrivateRoute from './components/PrivateRoute';
import CustomerDetails from './components/CustomerDetails'; // New import
import HardwareList from './components/HardwareList';     // New import
import HardwareSelected from './components/HardwareSelected';
import SystemReady from './components/SystemReady';
import VideoCall from './components/VideoCall';
import Payment from './components/Payment';
import Dispatch from './components/Dispatch';
import ExcelViewer from './components/ExcelFile';
import EntryForm from './components/EntryForm'; // New import
import AuthContext from './context/AuthContext';

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

function App() {
  const [open, setOpen] = React.useState(true);
  const location = useLocation();
  const { isAuthenticated, logout } = useContext(AuthContext);
  const [selectedHardware, setSelectedHardware] = React.useState(() => {
    const savedHardware = localStorage.getItem('selectedHardware');
    return savedHardware ? JSON.parse(savedHardware) : [];
  });

  const [systemReadyHardware, setSystemReadyHardware] = React.useState(() => {
    const savedReadyHardware = localStorage.getItem('systemReadyHardware');
    return savedReadyHardware ? JSON.parse(savedReadyHardware) : [];
  });

  const [videoCallHardware, setVideoCallHardware] = React.useState(() => {
    const savedVideoCallHardware = localStorage.getItem('videoCallHardware');
    return savedVideoCallHardware ? JSON.parse(savedVideoCallHardware) : [];
  });

  const [paymentHardware, setPaymentHardware] = React.useState(() => {
    const savedPaymentHardware = localStorage.getItem('paymentHardware');
    return savedPaymentHardware ? JSON.parse(savedPaymentHardware) : [];
  });

  const [dispatchedHardware, setDispatchedHardware] = React.useState(() => {
    const savedDispatchedHardware = localStorage.getItem('dispatchedHardware');
    return savedDispatchedHardware ? JSON.parse(savedDispatchedHardware) : [];
  });

  const [takenByState, setTakenByState] = React.useState(() => {
    const savedTakenBy = localStorage.getItem('takenByState');
    return savedTakenBy ? JSON.parse(savedTakenBy) : {};
  });

  useEffect(() => {
    localStorage.setItem('selectedHardware', JSON.stringify(selectedHardware));
  }, [selectedHardware]);

  useEffect(() => {
    localStorage.setItem('systemReadyHardware', JSON.stringify(systemReadyHardware));
  }, [systemReadyHardware]);

  useEffect(() => {
    localStorage.setItem('videoCallHardware', JSON.stringify(videoCallHardware));
  }, [videoCallHardware]);

  useEffect(() => {
    localStorage.setItem('paymentHardware', JSON.stringify(paymentHardware));
  }, [paymentHardware]);

  useEffect(() => {
    localStorage.setItem('dispatchedHardware', JSON.stringify(dispatchedHardware));
  }, [dispatchedHardware]);

  useEffect(() => {
    localStorage.setItem('takenByState', JSON.stringify(takenByState));
  }, [takenByState]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleHardwareSelect = (hardware) => {
    const itemIdentifier = `${hardware['Customer Name']}-${hardware['Serial No']}-${hardware['Date']}`;
    const isDuplicate = selectedHardware.some(
      (item) => `${item['Customer Name']}-${item['Serial No']}-${item['Date']}` === itemIdentifier
    );
    if (isDuplicate) {
      alert('This hardware is already selected.');
    } else {
      setSelectedHardware((prevSelected) => [...prevSelected, hardware]);
    }
  };

  const handleHardwareDelete = (hardware) => {
    const itemIdentifier = `${hardware['Customer Name']}-${hardware['Serial No']}-${hardware['Date']}`;
    setSelectedHardware((prevSelected) =>
      prevSelected.filter(
        (item) =>
          `${item['Customer Name']}-${item['Serial No']}-${item['Date']}` !== itemIdentifier
      )
    );
  };

  const handleSystemReady = (hardware) => {
    const itemIdentifier = `${hardware['Customer Name']}-${hardware['Serial No']}-${hardware['Date']}`;
    if (systemReadyHardware.find((item) => `${item['Customer Name']}-${item['Serial No']}-${item['Date']}` === itemIdentifier)) {
      alert('This hardware is already in System Ready.');
    } else {
      setSystemReadyHardware((prevReady) => [...prevReady, hardware]);
      setSelectedHardware((prevSelected) =>
        prevSelected.filter((item) => `${item['Customer Name']}-${item['Serial No']}-${item['Date']}` !== itemIdentifier)
      );
    }
  };

  const handleSystemReadyDelete = (hardware) => {
    const itemIdentifier = `${hardware['Customer Name']}-${hardware['Serial No']}-${hardware['Date']}`;
    setSystemReadyHardware((prevReady) =>
      prevReady.filter(
        (item) =>
          `${item['Customer Name']}-${item['Serial No']}-${item['Date']}` !== itemIdentifier
      )
    );
  };

  const handleClearSystemReady = () => {
    setSystemReadyHardware([]);
  };

  const handleVideoCallAdd = (hardware) => {
    console.log('Attempting to move hardware to Video Call:', hardware);
    const itemIdentifier = `${hardware['Customer Name']}-${hardware['Serial No']}-${hardware['Date']}`;
    if (videoCallHardware.find((item) => `${item['Customer Name']}-${item['Serial No']}-${item['Date']}` === itemIdentifier)) {
      alert('This hardware is already in Video Call.');
    } else {
      setVideoCallHardware((prevVideoCall) => [...prevVideoCall, hardware]);
      console.log('Before filtering System Ready:', systemReadyHardware);
      setSystemReadyHardware((prevReady) => {
        const newReady = prevReady.filter((item) => `${item['Customer Name']}-${item['Serial No']}-${item['Date']}` !== itemIdentifier);
        console.log('After filtering System Ready:', newReady);
        return newReady;
      });
    }
  };

  const handlePaymentAdd = (hardware) => {
    console.log('Attempting to move hardware to Payment:', hardware);
    const itemIdentifier = `${hardware['Customer Name']}-${hardware['Serial No']}-${hardware['Date']}`;
    if (paymentHardware.find((item) => `${item['Customer Name']}-${item['Serial No']}-${item['Date']}` === itemIdentifier)) {
      alert('This hardware is already in Payment.');
    } else {
      setPaymentHardware((prevPayment) => [...prevPayment, hardware]);
      console.log('Before filtering Video Call:', videoCallHardware);
      setVideoCallHardware((prevVideoCall) =>
        prevVideoCall.filter((item) => `${item['Customer Name']}-${item['Serial No']}-${item['Date']}` !== itemIdentifier)
      );
      console.log('After filtering Video Call:', videoCallHardware);
    }
  };







  const handleVideoCallDelete = (hardware) => {
    const itemIdentifier = `${hardware['Customer Name']}-${hardware['Serial No']}-${hardware['Date']}`;
    setVideoCallHardware((prevVideoCall) =>
      prevVideoCall.filter(
        (item) =>
          `${item['Customer Name']}-${item['Serial No']}-${item['Date']}` !== itemIdentifier
      )
    );
  };

  const handlePaymentDelete = (hardware) => {
    const itemIdentifier = `${hardware['Customer Name']}-${hardware['Serial No']}-${hardware['Date']}`;
    setPaymentHardware((prevPayment) =>
      prevPayment.filter(
        (item) =>
          `${item['Customer Name']}-${item['Serial No']}-${item['Date']}` !== itemIdentifier
      )
    );
    alert(`Hardware ${hardware['Serial No']} deleted from Payment.`);
  };

  const handleDispatchAdd = (hardware) => {
    console.log('Attempting to move hardware to Dispatch:', hardware);
    const itemIdentifier = `${hardware['Customer Name']}-${hardware['Serial No']}-${hardware['Date']}`;
    if (dispatchedHardware.find((item) => `${item['Customer Name']}-${item['Serial No']}-${item['Date']}` === itemIdentifier)) {
      alert('This hardware is already in Dispatch.');
    } else {
      setDispatchedHardware((prevDispatch) => [...prevDispatch, hardware]);
      console.log('Before filtering Payment:', paymentHardware);
      setPaymentHardware((prevPayment) =>
        prevPayment.filter((item) => `${item['Customer Name']}-${item['Serial No']}-${item['Date']}` !== itemIdentifier)
      );
      console.log('After filtering Payment:', paymentHardware);
    }
  };

  const handleCompleted = async (hardware) => {
    try {
      const serialNo = encodeURIComponent(hardware['Serial No']);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/entries/by-serial/${serialNo}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete hardware entry.');
      }

      const itemIdentifier = `${hardware['Customer Name']}-${hardware['Serial No']}-${hardware['Date']}`;
      setDispatchedHardware((prevDispatch) =>
        prevDispatch.filter(
          (item) =>
            `${item['Customer Name']}-${item['Serial No']}-${item['Date']}` !== itemIdentifier
        )
      );
    } catch (error) {
      console.error('Error completing hardware dispatch:', error);
      alert(`Failed to mark hardware as completed. Please try again. Error: ${error.message}`);
    }
  };

  const getTitle = () => {
    switch (location.pathname) {
      case '/':
        return 'Dashboard';
      case '/hardware':
        return 'Hardware List';
      case '/hardware-selected':
        return 'Hardware Selected';
      case '/system-ready':
        return 'System Ready';
      case '/video-call':
        return 'Video Call';
      case '/payment':
        return 'Payment';
      case '/dispatch':
        return 'Dispatch';

      case '/customer-details':
        return 'Customer Details';
      case '/admin/login':
        return 'Admin Login';
      case '/admin/register':
        return 'Admin Registration';
      default:
        return 'Computer System Admin';
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
        <AppBar position="fixed" open={open}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerToggle}
              edge="start"
              sx={{
                marginRight: 5,
                // ...(open && { display: 'none' }),
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              {getTitle()}
            </Typography>
          </Toolbar>
        </AppBar>
        <Drawer
          variant="persistent"
          anchor="left"
          open={open}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          <DrawerHeader>
            <IconButton onClick={handleDrawerToggle}>
              <MenuIcon />
            </IconButton>
          </DrawerHeader>
          <List>
            <ListItem disablePadding component={Link} to="/">
              <ListItemButton>
                <ListItemIcon>
                  <DashboardIcon />
                </ListItemIcon>
                <ListItemText primary="Dashboard" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding component={Link} to="/customer-details">
              <ListItemButton>
                <ListItemIcon>
                  <StorageIcon />
                </ListItemIcon>
                <ListItemText primary="Customer Details" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding component={Link} to="/hardware">
              <ListItemButton>
                <ListItemIcon>
                  <HardwareIcon />
                </ListItemIcon>
                <ListItemText primary="Hardware List" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding component={Link} to="/hardware-selected">
              <ListItemButton>
                <ListItemIcon>
                  <BuildCircleIcon />
                </ListItemIcon>
                <ListItemText primary="Hardware Selected" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding component={Link} to="/system-ready">
              <ListItemButton>
                <ListItemIcon>
                  <CheckCircleOutlineIcon />
                </ListItemIcon>
                <ListItemText primary="System Ready" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding component={Link} to="/video-call">
              <ListItemButton>
                <ListItemIcon>
                  <VideocamIcon />
                </ListItemIcon>
                <ListItemText primary="Video Call" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding component={Link} to="/payment">
              <ListItemButton>
                <ListItemIcon>
                  <PaymentIcon />
                </ListItemIcon>
                <ListItemText primary="Payment" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding component={Link} to="/dispatch">
              <ListItemButton>
                <ListItemIcon>
                  <LocalShippingIcon />
                </ListItemIcon>
                <ListItemText primary="Dispatch" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding component={Link} to="/excel-data">
              <ListItemButton>
                <ListItemIcon>
                  <InsertDriveFileIcon />
                </ListItemIcon>
                <ListItemText primary="Excel Data" />
              </ListItemButton>
            </ListItem>

            {!isAuthenticated && (
              <ListItem disablePadding component={Link} to="/admin/login">
                <ListItemButton>
                  <ListItemIcon>
                    <SettingsIcon /> {/* Using SettingsIcon for Admin Login */}
                  </ListItemIcon>
                  <ListItemText primary="Admin Login" />
                </ListItemButton>
              </ListItem>
            )}

            {!isAuthenticated && (
              <ListItem disablePadding component={Link} to="/admin/register">
                <ListItemButton>
                  <ListItemIcon>
                    <SettingsIcon /> {/* Using SettingsIcon for Admin Register */}
                  </ListItemIcon>
                  <ListItemText primary="Admin Registration" />
                </ListItemButton>
              </ListItem>
            )}

            {isAuthenticated && (
              <ListItem disablePadding onClick={logout}>
                <ListItemButton>
                  <ListItemIcon>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            transition: (theme) =>
              theme.transitions.create('margin', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            marginLeft: `-${drawerWidth}px`,
            ...(open && {
              transition: (theme) =>
                theme.transitions.create('margin', {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              marginLeft: 0,
            }),
          }}
        >
          <Toolbar /> {/* This is to offset content below the AppBar */}
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/register" element={<AdminRegister />} />
            <Route element={<PrivateRoute />}>
              <Route path="/" element={<Dashboard
                selectedHardwareCount={selectedHardware.length}
                systemReadyHardwareCount={systemReadyHardware.length}
                videoCallHardwareCount={videoCallHardware.length}
                paymentHardwareCount={paymentHardware.length}
                dispatchedHardwareCount={dispatchedHardware.length}
                onHardwareSelect={handleHardwareSelect}
              />} />
              <Route path="/customer-details" element={<CustomerDetails selectedHardware={selectedHardware} takenByState={takenByState} setTakenByState={setTakenByState} />} />
              <Route path="/hardware" element={<HardwareList onHardwareSelect={handleHardwareSelect} selectedHardware={selectedHardware} takenByState={takenByState} setTakenByState={setTakenByState} />} />
              <Route path="/hardware-selected" element={<HardwareSelected selectedHardware={selectedHardware} onHardwareDelete={handleHardwareDelete} onSystemReady={handleSystemReady} takenByState={takenByState} />} />
              <Route path="/system-ready" element={<SystemReady systemReadyHardware={systemReadyHardware} onSystemReadyDelete={handleSystemReadyDelete} onVideoCallAdd={handleVideoCallAdd} onClearSystemReady={handleClearSystemReady} />} />
              <Route path="/video-call" element={<VideoCall videoCallHardware={videoCallHardware} onVideoCallDelete={handleVideoCallDelete} onPaymentAdd={handlePaymentAdd} />} />
              <Route path="/payment" element={<Payment paymentHardware={paymentHardware} onPaymentDelete={handlePaymentDelete} onDispatchAdd={handleDispatchAdd} />} />
              <Route path="/dispatch" element={<Dispatch dispatchedHardware={dispatchedHardware} onCompleted={handleCompleted} />} />
              <Route path="/excel-data" element={<ExcelViewer />} />
              <Route path="/systems/new" element={<EntryForm />} />
            </Route>
          </Routes>
        </Box>
    </Box>
  );
}

export default App;
